import React, { useEffect, useState } from "react";
import { Printer, Rows3, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";

// BulkPrintDialog — the "quantity per selected product" step between ticking
// rows on the Inventory table and the actual /admin/print-tags output. Each
// row starts at qty=1 (NOT auto-filled to current stock — printing should
// always be a deliberate, explicit choice, never an accidental full-stock
// reprint). On submit we look up existing piece barcodes via the READ-ONLY
// /inventory/pieces/print-batch endpoint and hand the list to the Print Tags
// page. That endpoint never creates inventory records — if a product doesn't
// have enough already-tracked pieces for the requested quantity, fewer tags
// come back and we surface that shortfall clearly instead of silently
// fabricating new stock.
export function BulkPrintDialog({ open, onOpenChange, products, onPrinted }) {
  const [quantities, setQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const initial = {};
      products.forEach((p) => { initial[p.id] = 1; });
      setQuantities(initial);
    }
  }, [open, products]);

  const setQty = (id, val) => {
    const n = Math.max(1, Math.min(500, Math.floor(Number(val) || 1)));
    setQuantities((prev) => ({ ...prev, [id]: n }));
  };

  const setAllToStock = () => {
    const next = {};
    products.forEach((p) => { next[p.id] = Math.max(1, Number(p.stock) || 1); });
    setQuantities(next);
  };
  const setAllToOne = () => {
    const next = {};
    products.forEach((p) => { next[p.id] = 1; });
    setQuantities(next);
  };

  const totalTags = products.reduce((sum, p) => sum + (Number(quantities[p.id]) || 0), 0);

  const doPrint = async () => {
    if (!products.length) return;
    setSubmitting(true);
    try {
      const items = products.map((p) => ({ product_id: p.id, quantity: quantities[p.id] || 1 }));
      const { data } = await api.post("/inventory/pieces/print-batch", { items });
      if (!data.barcodes?.length) {
        toast.error("None of the selected products have any pieces with a barcode yet. Add pieces via the barcode icon on Inventory first.");
        return;
      }
      // Large batches can exceed a safe URL length — stash the barcode list in
      // sessionStorage and hand the print page a short lookup key instead.
      // IMPORTANT: do NOT pass "noopener" here — sessionStorage is only
      // inherited by a same-origin popup when the opener relationship is kept.
      // With "noopener" the new tab gets a brand-new (empty) session storage
      // and the lookup below would always report "expired".
      const batchKey = `mj_print_batch_${Date.now()}`;
      sessionStorage.setItem(batchKey, JSON.stringify(data.barcodes));
      window.open(`/admin/print-tags?batch=${encodeURIComponent(batchKey)}`, "_blank");

      const shortfalls = (data.summary || []).filter((r) => (r.shortfall || 0) > 0);
      if (shortfalls.length) {
        const missing = shortfalls.reduce((s, r) => s + r.shortfall, 0);
        toast.warning(
          `Printed ${data.barcodes.length} tag(s) — ${missing} fewer than requested because ${shortfalls.length} product(s) don't have enough tracked pieces yet. Add pieces via the barcode icon on Inventory to print the rest.`
        );
      } else {
        toast.success(`${data.barcodes.length} tag(s) ready to print`);
      }
      onPrinted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not prepare tags for printing");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-print-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Printer className="h-5 w-5 text-[hsl(var(--primary))]" /> Print Selected Tags
          </DialogTitle>
          <DialogDescription>
            Choose how many tags to print for each selected design, then send them to the tag printer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-muted-foreground">{products.length} product(s) selected</div>
          <div className="flex items-center gap-2">
            <Button data-testid="bulk-print-qty-all-one" size="sm" variant="outline" onClick={setAllToOne}>1 each</Button>
            <Button data-testid="bulk-print-qty-all-stock" size="sm" variant="outline" onClick={setAllToStock}>Full stock</Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-border">
          <Table data-testid="bulk-print-table">
            <TableHeader>
              <TableRow className="bg-[#F4EBDD]">
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>In Stock</TableHead>
                <TableHead className="w-32">Qty to Print</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} className="hover:bg-[#F7F0E6]">
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                  <TableCell>
                    {p.stock <= 3 ? <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">{p.stock}</Badge> : <span>{p.stock}</span>}
                  </TableCell>
                  <TableCell>
                    <Input data-testid="bulk-print-qty-input" type="number" min="1" max="500" step="1"
                           value={quantities[p.id] ?? 1}
                           onChange={(e) => setQty(p.id, e.target.value)}
                           className="h-8 font-mono text-sm" />
                  </TableCell>
                </TableRow>
              ))}
              {products.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No products selected.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {products.some((p) => (quantities[p.id] || 0) > (Number(p.stock) || 0)) && (
          <div className="flex items-start gap-2 rounded-lg border border-[#E6D3AA] bg-[#F7F0E6] p-3 text-xs text-[#8E6F33]">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Some quantities exceed current stock. Printing only creates tags for pieces that already have a barcode on file — it never creates new stock. If some products don't have enough tracked pieces yet, you'll get fewer tags than requested; add pieces first via the barcode icon on Inventory.</span>
          </div>
        )}

        <div className="rounded-lg bg-[#F7F0E6] p-3 text-xs text-muted-foreground flex items-center gap-2">
          <Rows3 className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
          <span>Prints one 100 × 15 mm CD410 hang tag per piece, back-to-back on the roll.</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="bulk-print-generate-button" onClick={doPrint} disabled={submitting || !products.length} className="gap-2">
            <Printer className="h-4 w-4" /> {submitting ? "Preparing..." : `Print ${totalTags} Tag${totalTags !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
